import React, { FC, useCallback, useEffect, useState } from 'react';
import { LayoutBox } from 'src/common/components/LayoutBox';
import { useResetRedirectUrl } from 'src/common/guards/useResetRedirectUrl';
import styled, { css } from 'styled-components';
import { colors } from 'src/config/colors';
import { CleanButton } from 'src/common/components/Button';
import { media, widthBreakpoints } from 'src/config/breakpoints';
import { useMediaQuery } from 'src/common/migration/useMediaQuery';
import {
  courseListingSelectedFiltersCountSelector,
  courseListingViewType,
} from 'src/state/courseListing/courseListingSelectors';
import { useDispatch, useSelector } from 'react-redux';
import { Layout } from 'src/common/components/Layout';
import { CourseListingFilters } from 'src/public/components/course-listing/CourseListingFilters';
import { CourseListingFiltersModal } from 'src/public/components/course-listing/CourseListingFiltersModal';
import { CourseListingHeading } from 'src/public/components/course-listing/CourseListingHeading';
import { courseListingActions } from 'src/state/courseListing/courseListingActions';
import { CourseListingToggleCourse } from 'src/public/components/course-listing/CourseListingToggleCourse';
import { CourseViewType } from 'src/state/courseListing/courseListingReducer';
import { CourseListingAllCoursesList } from 'src/public/components/course-listing/CourseListingAllCoursesList';
import { CourseListingCourseTypeListPagination } from 'src/public/components/course-listing/CourseListingCourseTypeListPagination';
import { PageProps } from 'src/common/migration/PageProps';
import { getCourseCategories, getCourseCategoriesDTO } from 'src/common/services/course';
import { HomepageRoute } from 'src/public/publicRoutes';
import { mapToCategory } from 'src/common/models/category';
import { getSlug } from 'src/common/services/slug';
import { VendorGetStaticProps } from 'vendor/utils/VendorGetStaticProps';
import { GetStaticPathsResult } from 'next';
import { useSearchParams } from 'src/common/utils/useSearchParams';
import { useClientReady } from 'src/lib/SSR/ClientOnly/ClientOnly';
import { buildUnauthenticatedUserKeyEnhancer } from 'src/common/hooks/useCurrentUserKeyEnhancer';
import { QueryKey } from 'src/config/queryKey';
import { firstPageNmb, firstRequestSize } from 'src/config/listing';
import { listEventsOnline } from 'src/common/services/event';
import { listElearnings } from 'src/common/services/elearning';
import { SearchRoute } from 'src/search/routes';
import { useActiveCourseCategoryFilter } from 'src/public/components/course-page/hooks/useActiveCourseCategoryFilter';
import { coerceInt } from 'src/lib/coerceInt';
import { getCourseBundles } from 'src/common/services/courseBundle';

const StyledLayoutBox = styled(LayoutBox)`
  min-height: 100%;
  padding-bottom: 3.75rem;
`;

const FilterButton = styled(CleanButton)`
  color: ${colors.pfp3bis};
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;

  ${media.w.min.px768(css`
    font-size: 1rem;
    line-height: 1.375rem;
  `)};

  ${media.w.min.px992(css`
    display: none;
  `)};
`;

const ListingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.25rem;

  ${media.w.min.px768(css`
    margin-top: -0.8125rem;
    padding: 0 1.5rem;
  `)};

  ${media.w.min.px992(css`
    margin: 0;
    padding: 2.375rem 0 1.25rem;
  `)};
`;

type SearchPageProps = {
  categoryId?: number;
};

const SearchPage: FC<PageProps<SearchPageProps>> = ({ categoryId }) => {
  useResetRedirectUrl();

  const dispatch = useDispatch();
  const [stable, setStable] = useState(true);
  const selectedViewType = useSelector(courseListingViewType);
  const clientReady = useClientReady();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (clientReady) {
      if (categoryId) {
        dispatch(courseListingActions.selectCategory(categoryId));
        dispatch(courseListingActions.setShowAllCategories(false));
      }

      if (searchParams) {
        if (searchParams.speakerId) {
          dispatch(courseListingActions.toggleSpeaker(coerceInt(searchParams.speakerId)));
        }

        if (searchParams.viewType) {
          dispatch(
            courseListingActions.toggleCourseTypeView(
              String(searchParams.viewType) as CourseViewType,
            ),
          );
        }
      }

      setStable(true);

      return () => {
        dispatch(courseListingActions.clearFilters());
      };
    }
  }, [clientReady, searchParams]);

  const isDesktop = useMediaQuery({
    minWidth: widthBreakpoints.px992,
  });
  const [showModal, setShowModal] = useState(false);
  const selectedFiltersCount = useSelector(courseListingSelectedFiltersCountSelector);
  const activeCourseCategoryFilter = useActiveCourseCategoryFilter();

  const handleModalClose = useCallback(() => setShowModal(false), [setShowModal]);

  useEffect(() => setShowModal(false), [isDesktop]);

  return (
    <Layout
      leftPanel={<CourseListingFilters />}
      scrollToTopButton
      title={
        activeCourseCategoryFilter.length === 1
          ? `${activeCourseCategoryFilter[0].title} | Szkolenia`
          : 'Szkolenia'
      }
    >
      <StyledLayoutBox>
        <ListingHeader>
          <CourseListingHeading />
          <FilterButton onClick={() => setShowModal(true)}>
            FILTRY {selectedFiltersCount > 0 && <span>({selectedFiltersCount})</span>}
          </FilterButton>
        </ListingHeader>
        <CourseListingToggleCourse />
        {stable && (
          <>
            {selectedViewType === CourseViewType.All ? (
              <CourseListingAllCoursesList />
            ) : (
              <CourseListingCourseTypeListPagination viewType={selectedViewType} />
            )}
          </>
        )}
      </StyledLayoutBox>

      {showModal && <CourseListingFiltersModal onClose={handleModalClose} />}
    </Layout>
  );
};

export const getStaticProps = VendorGetStaticProps<SearchPageProps>(async (ctx) => {
  if (!ctx.params) {
    return {
      redirect: {
        permanent: true,
        destination: HomepageRoute,
      },
    };
  }

  const categorySlug = ctx.params.categorySlug;
  const categories = await getCourseCategoriesDTO();
  const categoryFound = categories.find((c) => getSlug(mapToCategory(c)) === categorySlug);

  if (!categoryFound) {
    return {
      redirect: {
        permanent: false,
        destination: SearchRoute,
      },
    };
  }

  const elearningCoursesFilters = { categoryIds: [categoryFound.id] };
  const onlineCoursesFilters = { categoryIds: [categoryFound.id] };
  const { key: elearningCoursesKey } = buildUnauthenticatedUserKeyEnhancer([
    QueryKey.ElearningEventList,
    elearningCoursesFilters,
    firstPageNmb,
    firstRequestSize,
  ]);
  const { key: onlineCoursesKey } = buildUnauthenticatedUserKeyEnhancer([
    QueryKey.CoursesEventList,
    onlineCoursesFilters,
    firstPageNmb,
    firstRequestSize,
  ]);
  const { key: courseBundlesKey } = buildUnauthenticatedUserKeyEnhancer([
    QueryKey.CoursesEventList,
    firstPageNmb,
    firstRequestSize,
  ]);

  await ctx.queryClient.prefetchQuery(elearningCoursesKey, () =>
    listElearnings(elearningCoursesFilters, firstPageNmb, firstRequestSize, false),
  );

  await ctx.queryClient.prefetchQuery(onlineCoursesKey, () =>
    listEventsOnline(onlineCoursesFilters, firstPageNmb, firstRequestSize, false),
  );

  await ctx.queryClient.prefetchQuery(courseBundlesKey, () =>
    getCourseBundles({}, firstPageNmb, firstRequestSize),
  );

  ctx.store.dispatch(courseListingActions.toggleCategory(categoryFound.id));
  ctx.store.dispatch(courseListingActions.setShowAllCategories(false));

  return {
    props: {
      categoryId: categoryFound.id,
    },
  };
});

export async function getStaticPaths(): Promise<GetStaticPathsResult<{ categorySlug: string }>> {
  const categories = await getCourseCategories();
  return {
    paths: categories.map((category) => {
      return {
        params: { categorySlug: getSlug(category) },
      };
    }),
    fallback: 'blocking',
  };
}

export default SearchPage;
